/**
 * NomineeSessionContext - Session management for nominee users
 * Features:
 * - Multi-tab sync via digitalvault-session-nominee
 * - Inactivity timeout: 20 minutes
 * - Activity tracking
 * - Automatic logout on session expiry
 * - 401 interceptor
 * - Nominee-specific storage cleanup
 */

import React, { createContext, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import InactivityWarning from '../components/InactivityWarning';
import logger from '../utils/logger';
import apiClient from '../utils/apiConfig';
import {
  initTabSync,
  stopTabSync,
  broadcastLogout,
  broadcastSessionExtended
} from '../utils/tabSync';
import { handleRemoteActivity } from '../utils/activityTracker';
import { getRoleChannelName, getRoleInactivityTimeout, SESSION_TYPES, clearRoleSession } from '../utils/sessionManager';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHANNEL_NAME = getRoleChannelName(SESSION_TYPES.NOMINEE);
const INACTIVITY_TIMEOUT_MINUTES = getRoleInactivityTimeout(SESSION_TYPES.NOMINEE);

const INACTIVITY_TIMEOUT_ENABLED = import.meta.env.VITE_ENABLE_INACTIVITY_TIMEOUT !== 'false';
const TAB_SYNC_ENABLED = import.meta.env.VITE_ENABLE_TAB_SYNC !== 'false';

const NomineeSessionContext = createContext(null);

export const NomineeSessionProvider = ({ children, nomineeData, onLogout }) => {
  const handleInactivityLogout = useCallback(async () => {
    logger.log('Nominee inactive - triggering auto logout');

    // Broadcast logout to other nominee tabs FIRST
    if (TAB_SYNC_ENABLED) {
      broadcastLogout('inactivity', CHANNEL_NAME);
    }

    toast.warning('You have been logged out due to inactivity.', {
      position: 'top-center',
      autoClose: 5000
    });

    try {
      const token = localStorage.getItem('nomineeToken');
      if (token) {
        await axios.post(`${API}/nominee/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      logger.error('Nominee logout API call failed:', error);
    }

    // Clear nominee-specific storage
    clearRoleSession(SESSION_TYPES.NOMINEE);

    if (onLogout) {
      onLogout();
    }

    window.location.href = '/nominee/login';
  }, [onLogout]);

  const {
    showWarning,
    warningSeconds,
    handleStayLoggedIn,
    handleAutoLogout
  } = useInactivityTimeout(
    handleInactivityLogout,
    INACTIVITY_TIMEOUT_MINUTES,
    CHANNEL_NAME,
    INACTIVITY_TIMEOUT_ENABLED && !!nomineeData
  );

  // Setup 401 interceptor for both axios and apiClient
  useEffect(() => {
    const handle401Error = (error) => {
      if (error.response?.status === 401) {
        const requestUrl = error.config?.url || '';

        // Skip interceptor for nominee login + activation endpoints.
        // These pages handle their own error messages; a 401 from activation
        // means wrong key/answers, not an expired session (used by the
        // dashboard's Link Vault flow too).
        const isNomineeLoginEndpoint = requestUrl.includes('/nominee/login/') ||
                                       requestUrl.includes('/nominee/activate/');

        if (isNomineeLoginEndpoint) {
          // Let the login page handle the error
          return Promise.reject(error);
        }

        logger.log('Nominee session expired (401) - forcing logout');
        toast.error('Your nominee session has expired. Please login again.', {
          position: 'top-center',
          autoClose: 5000
        });

        if (TAB_SYNC_ENABLED) {
          broadcastLogout('session_expired', CHANNEL_NAME);
        }

        // Clear nominee-specific storage
        clearRoleSession(SESSION_TYPES.NOMINEE);

        if (onLogout) {
          onLogout();
        }

        window.location.href = '/nominee/login';
      }
      return Promise.reject(error);
    };

    // Interceptor for global axios instance
    const axiosInterceptor = axios.interceptors.response.use(
      response => response,
      handle401Error
    );

    // Interceptor for apiClient instance
    const apiClientInterceptor = apiClient.interceptors.response.use(
      response => response,
      handle401Error
    );

    return () => {
      axios.interceptors.response.eject(axiosInterceptor);
      apiClient.interceptors.response.eject(apiClientInterceptor);
    };
  }, [onLogout]);

  // Setup tab sync
  useEffect(() => {
    if (!TAB_SYNC_ENABLED || !nomineeData) return;

    const tabSyncCallbacks = {
      // When another tab is active, sync the activity
      onActivity: (data) => {
        logger.log('[NomineeTabSync] Activity detected in another tab, syncing...');
        handleRemoteActivity(data);
      },

      // When another tab shows warning, just log it
      onWarning: (data) => {
        logger.log('[NomineeTabSync] Warning shown in another tab');
      },

      // When another tab logs out, logout this tab too
      onLogout: (data) => {
        logger.log('[NomineeTabSync] Logout detected in another tab, logging out...');
        toast.info('You have been logged out from another nominee tab.', {
          position: 'top-center',
          autoClose: 3000
        });

        // Clear nominee-specific storage
        clearRoleSession(SESSION_TYPES.NOMINEE);

        if (onLogout) {
          onLogout();
        }

        // Small delay to show toast
        setTimeout(() => {
          window.location.href = '/nominee/login';
        }, 500);
      },

      // When another tab extends session, sync here
      onSessionExtended: () => {
        logger.log('[NomineeTabSync] Session extended in another tab, syncing...');
        handleRemoteActivity({ timestamp: Date.now() });
        toast.success('Nominee session extended in another tab!', {
          position: 'top-center',
          autoClose: 2000
        });
      }
    };

    const syncInitialized = initTabSync(tabSyncCallbacks, CHANNEL_NAME);
    if (syncInitialized) {
      logger.log('[NomineeSessionProvider] Multi-tab sync enabled for nominee');
    }

    return () => {
      stopTabSync(CHANNEL_NAME);
    };
  }, [nomineeData, onLogout]);

  const handleStayLoggedInWithToast = useCallback(() => {
    handleStayLoggedIn();

    // Broadcast to other tabs
    if (TAB_SYNC_ENABLED) {
      broadcastSessionExtended(CHANNEL_NAME);
    }

    toast.success('Nominee session extended!', {
      position: 'top-center',
      autoClose: 2000
    });
  }, [handleStayLoggedIn]);

  return (
    <NomineeSessionContext.Provider value={{
      inactivityTimeout: INACTIVITY_TIMEOUT_MINUTES,
      channelName: CHANNEL_NAME,
      inactivityEnabled: INACTIVITY_TIMEOUT_ENABLED,
      tabSyncEnabled: TAB_SYNC_ENABLED
    }}>
      {children}

      {INACTIVITY_TIMEOUT_ENABLED && (
        <InactivityWarning
          show={showWarning}
          secondsRemaining={warningSeconds}
          onStayLoggedIn={handleStayLoggedInWithToast}
          onLogout={handleAutoLogout}
          roleType="Nominee"
        />
      )}
    </NomineeSessionContext.Provider>
  );
};


export const useNomineeSession = () => {
  const context = useContext(NomineeSessionContext);
  if (!context) {
    throw new Error('useNomineeSession must be used within NomineeSessionProvider');
  }
  return context;
};
